import { Fragment, ReactNode } from "react";

import type { FlagGetter } from "@/FeatureFlag";
import { RoleAdmin, RoleInviteDefault } from "@/components/users/inviteUser";
import { isFeatureUnlocked } from "@/features/registry";

const RoleGeneralUser = RoleInviteDefault;
const RoleDataManager = "data_manager";
const RoleEventManager = "event_manager";
const RoleDataViewer = "data_viewer";

export type RoleOption = {
  value: string;
  label: string;
  description: (
    flag: FlagGetter,
    hasSessionReplayHeatmapsEntitlement: boolean,
  ) => ReactNode;
  rank: number;
};

// Session Replay, Heatmaps, and Surveys all sit behind the single
// heatmapssurveysandsessionreplay RBAC domain, so a role reaches all three or
// none of them. The clause names only the products the account actually has, and
// drops out entirely when it has none, so we don't advertise permissions for
// things the reader can't reach yet.
//
// This reads two sources rather than one because the products unlock
// differently: Session Replay and Heatmaps both ride the shared
// sessionReplayAndHeatmaps entitlement (IND-2855, IND-3020), while Surveys
// unlocks through the CRO feature registry (SurveysPage.tsx resolves it via
// useFeatureAccess). isFeatureUnlocked keeps that mapping in one place.
const sharedDomainFeatures = (
  flag: FlagGetter,
  hasSessionReplayHeatmapsEntitlement: boolean,
): string[] => {
  const features: string[] = [];

  if (hasSessionReplayHeatmapsEntitlement) {
    features.push("session replays");
    features.push("heatmaps");
  }

  if (isFeatureUnlocked("surveys", flag)) {
    features.push("surveys");
  }

  return features;
};

// Deliberately no serial comma before the final conjunction. These clauses are
// nested inside a list that is itself comma-separated and joined by "or", so
// "session replays, heatmaps, or surveys, or view PHI" leaves the reader unable
// to tell where the inner list ends. "heatmaps and surveys" binds tightly enough
// to survive the nesting. At two products the output is unchanged either way.
const joinFeatures = (
  features: string[],
  conjunction: "and" | "or",
): string => {
  if (features.length < 2) {
    return features.join("");
  }

  const last = features[features.length - 1];
  return `${features.slice(0, -1).join(", ")} ${conjunction} ${last}`;
};

// The domain grants read and write together, and write is what reaches the
// Session Replay settings surface, which pauses and resumes capture for the
// whole account. "view and manage" matches how the rest of these sentences
// describe a read+write domain ("view and manage web analytics"). The denied
// side stays at "view" because a role with no read has nothing to manage.
const grantedSharedDomainClause = (
  flag: FlagGetter,
  hasSessionReplayHeatmapsEntitlement: boolean,
): string => {
  const features = sharedDomainFeatures(
    flag,
    hasSessionReplayHeatmapsEntitlement,
  );

  return features.length > 0
    ? `view and manage ${joinFeatures(features, "and")}, `
    : "";
};

const deniedSharedDomainClause = (
  flag: FlagGetter,
  hasSessionReplayHeatmapsEntitlement: boolean,
): string => {
  const features = sharedDomainFeatures(
    flag,
    hasSessionReplayHeatmapsEntitlement,
  );

  return features.length > 0 ? `view ${joinFeatures(features, "or")}, ` : "";
};

const basicRoleOptions: RoleOption[] = [
  {
    value: RoleAdmin,
    label: "Admin",
    description: () => (
      <Fragment>
        Members that need full access to make changes to the platform including
        inviting users and administering permissions.
      </Fragment>
    ),
    rank: 1,
  },
  {
    value: RoleGeneralUser,
    label: "General User",
    description: (flag, hasSessionReplayHeatmapsEntitlement) => (
      <Fragment>
        Members that need to view and manage web analytics, view Insights, Ad
        Performance, and CMP, configure destinations and events, manage videos,
        access web tracker monitoring, view and create audiences,{" "}
        {grantedSharedDomainClause(flag, hasSessionReplayHeatmapsEntitlement)}
        and view PHI,{" "}
        <strong>
          but do not need to configure consent management, manage other users,
          or modify allowlists.
        </strong>
      </Fragment>
    ),
    rank: 4,
  },
  {
    value: RoleDataViewer,
    label: "Data Viewer",
    description: (flag, hasSessionReplayHeatmapsEntitlement) => (
      <Fragment>
        Members that need to view web analytics, Insights, Ad Performance, and
        CMP{" "}
        <strong>
          but do not need to manage web analytics, view PHI, modify allowlists,
          configure destinations or events, configure consent management, manage
          videos, access web tracker monitoring, view and create audiences,{" "}
          {deniedSharedDomainClause(flag, hasSessionReplayHeatmapsEntitlement)}
          or manage other users
        </strong>
        .
      </Fragment>
    ),
    rank: 5,
  },
];

const advancedRoleOptions: RoleOption[] = [
  {
    value: RoleDataManager,
    label: "Data Manager",
    description: (flag, hasSessionReplayHeatmapsEntitlement) => (
      <Fragment>
        Members that need to view and manage web analytics, view Insights, Ad
        Performance, and CMP, modify allowlists, configure destinations and
        events, configure consent management, manage videos, access web tracker
        monitoring, view and create audiences,{" "}
        {grantedSharedDomainClause(flag, hasSessionReplayHeatmapsEntitlement)}
        and view PHI, <strong>but do not need to manage other users</strong>.
      </Fragment>
    ),
    rank: 2,
  },
  {
    value: RoleEventManager,
    label: "Event Manager",
    description: (flag, hasSessionReplayHeatmapsEntitlement) => (
      <Fragment>
        Members that need to view Ad Performance and CMP, configure destinations
        and events, monitor destination delivery, manage videos, view and sync
        audiences, and access web tracker monitoring,{" "}
        <strong>
          but do not need to view web analytics or Insights, configure consent
          management, manage other users, modify allowlists, create audiences,{" "}
          {deniedSharedDomainClause(flag, hasSessionReplayHeatmapsEntitlement)}
          or view PHI
        </strong>
        .
      </Fragment>
    ),
    rank: 3,
  },
];

export const getRoleOptions = (): RoleOption[] => {
  const compare = (a: RoleOption, b: RoleOption) => {
    if (a.rank < b.rank) {
      return -1;
    }
    if (a.rank > b.rank) {
      return 1;
    }
    return 0;
  };

  return basicRoleOptions.concat(advancedRoleOptions).sort(compare);
};

export const roleToReadableName = (role: string): string => {
  const allRoleOptions = basicRoleOptions.concat(advancedRoleOptions);
  const roleEntry = allRoleOptions.find(
    (roleOption) => roleOption.value === role,
  );
  return roleEntry ? roleEntry.label : role;
};
